<template>
  <div>
    <!-- Hero banner -->
    <page-hero
        class="mb-1"
        :title="msg('Room Settings')"
        :subtitle="msg('Room categories')"
        variant="realestate"
        :stats="[{ icon: 'GridIcon', value: categories.length, label: msg('roomcategories') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="15" y="25" width="70" height="80" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.3)" stroke-width="1.5"/>
          <rect x="25" y="40" width="22" height="18" rx="3" fill="rgba(255,255,255,.35)"/>
          <rect x="53" y="40" width="22" height="18" rx="3" fill="rgba(255,255,255,.25)"/>
          <rect x="25" y="65" width="22" height="18" rx="3" fill="rgba(255,255,255,.25)"/>
          <rect x="53" y="65" width="22" height="18" rx="3" fill="rgba(255,255,255,.35)"/>
        </svg>
      </template>
    </page-hero>

    <b-card no-body class="rs-card">
      <div class="rs-toolbar">
        <div class="rs-toolbar__hint">{{ msg('Room categories') }}</div>
        <b-button variant="primary" size="sm" @click="openNew">
          <feather-icon icon="PlusIcon" class="mr-25"/>
          {{ msg('roomcategory') }}
        </b-button>
      </div>

      <!-- Empty state -->
      <div v-if="!loading && categories.length === 0" class="rs-empty">
        <feather-icon icon="GridIcon" size="34" class="rs-empty__icon"/>
        <p class="rs-empty__title">{{ msg('Room categories') }}</p>
        <p class="rs-empty__text">{{ msg('roomcategoriesintro') }}</p>
        <b-button variant="primary" @click="openNew">{{ msg('roomcategory') }}</b-button>
      </div>

      <!-- Category grid -->
      <div v-else class="rs-grid">
        <div v-for="cat in categories" :key="cat.id" class="rs-cat" @click="openEdit(cat)">
          <div class="rs-cat__stripe" :style="{backgroundColor: cat.color || '#7367f0'}"></div>
          <div class="rs-cat__body">
            <div class="rs-cat__head">
              <span class="rs-cat__name">{{ cat.name }}</span>
              <b-badge v-if="cat.basePricePerNight != null" variant="light-success" class="rs-cat__price">
                {{ cat.basePricePerNight }} € / {{ msg('night') }}
              </b-badge>
              <b-badge v-else variant="light-secondary" class="rs-cat__price">{{ msg('No price') }}</b-badge>
            </div>
            <p class="rs-cat__desc" v-if="cat.description">{{ cat.description }}</p>
            <div class="rs-cat__meta">
              <span v-if="cat.sizeM2 != null" class="rs-cat__chip">
                <feather-icon icon="MaximizeIcon" size="13"/>{{ cat.sizeM2 }} m²
              </span>
              <span v-if="cat.beds != null" class="rs-cat__chip">
                <i class="fas fa-bed"></i>{{ cat.beds }} {{ msg('Beds') }}
              </span>
              <span v-if="cat.persons != null" class="rs-cat__chip">
                <feather-icon icon="UsersIcon" size="13"/>{{ cat.persons }} {{ msg('persons') }}
              </span>
              <span v-if="cat.cleaningFee != null" class="rs-cat__chip">
                <i class="fas fa-broom"></i>{{ cat.cleaningFee }} €
              </span>
            </div>
          </div>
          <div class="rs-cat__actions">
            <b-button variant="flat-danger" class="btn-icon" size="sm" @click.stop="confirmDelete(cat)">
              <feather-icon icon="Trash2Icon"/>
            </b-button>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Create / edit modal -->
    <b-modal v-model="dialogs.edit" :title="msg('roomcategory')" size="lg" centered no-close-on-backdrop hide-footer>
      <validation-observer ref="categoryForm" v-slot="{ passes }">
        <b-row>
          <b-col cols="12" md="8">
            <b-form-group :label="msg('Name')" label-for="rs-name">
              <validation-provider #default="{ errors }" :name="msg('Name')" rules="required">
                <b-form-input id="rs-name" v-model="category.name" :state="errors.length > 0 ? false:null"
                              :placeholder="msg('roomcategory')"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group :label="msg('color')" label-for="rs-color">
              <b-form-input id="rs-color" v-model="category.color" type="color" class="rs-colorpick"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group :label="msg('roomsize')" label-for="rs-size">
              <b-form-input id="rs-size" v-model.number="category.sizeM2" type="number" min="0" step="0.5"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group :label="msg('Beds')" label-for="rs-beds">
              <b-form-input id="rs-beds" v-model.number="category.beds" type="number" min="0" step="1"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group :label="msg('persons')" label-for="rs-persons">
              <b-form-input id="rs-persons" v-model.number="category.persons" type="number" min="0" step="1"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group :label="msg('Rooms')" label-for="rs-rooms">
              <b-form-input id="rs-rooms" v-model.number="category.rooms" type="number" min="0" step="1"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="4">
            <b-form-group :label="msg('basepricepernight')" label-for="rs-price">
              <validation-provider #default="{ errors }" :name="msg('basepricepernight')" rules="between:0.01,999999">
                <b-form-input id="rs-price" v-model.number="category.basePricePerNight" type="number" min="0" step="0.01"
                              :state="errors.length > 0 ? false:null"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="4">
            <b-form-group :label="msg('Cleaning fee')" label-for="rs-cleaning">
              <b-form-input id="rs-cleaning" v-model.number="category.cleaningFee" type="number" min="0" step="0.01"/>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="4">
            <b-form-group :label="msg('Sort order')" label-for="rs-sort">
              <b-form-input id="rs-sort" v-model.number="category.sortOrder" type="number" step="1"/>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="msg('description')" label-for="rs-desc">
              <b-form-textarea id="rs-desc" v-model="category.description" rows="2"/>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="rs-modal-hint">
          <feather-icon icon="InfoIcon" size="14"/>
          {{ msg('roomcategorypricehint') }}
        </div>
        <div class="text-right mt-1">
          <b-button variant="outline-secondary" class="mr-1" @click="dialogs.edit = false">{{ msg('cancel') }}</b-button>
          <b-button variant="primary" :disabled="saving" @click="passes(save)">{{ msg('save') }}</b-button>
        </div>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero'

export default {
  name: 'RoomSettings',
  components: {PageHero},
  data() {
    return {
      loading: true,
      saving: false,
      categories: [],
      category: {},
      dialogs: {edit: false},
      listeners: {refresh: () => {}},
    }
  },
  created() {
    this.refresh();
    this.listeners.refresh = () => this.refresh();
    this.$root.$on('crm::branch-updated', this.listeners.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh);
  },
  methods: {
    refresh() {
      let $this = this;
      this.loading = true;
      this.$store.dispatch('roomCategory/getAllForOrg')
          .then(categories => {
            $this.categories = (categories || []).slice()
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.name || '').localeCompare(b.name || ''));
          })
          .finally(() => $this.loading = false);
    },
    openNew() {
      // rooms defaults to 1: a hotel room is a single-room unit (unlike apartments)
      this.category = {color: '#7367f0', rooms: 1};
      this.dialogs.edit = true;
    },
    openEdit(cat) {
      this.category = {...cat};
      this.dialogs.edit = true;
    },
    save() {
      let $this = this;
      this.saving = true;
      const action = this.category.id == null ? 'roomCategory/add' : 'roomCategory/update';
      this.$store.dispatch(action, this.category)
          .then(() => {
            $this.dialogs.edit = false;
            $this.refresh();
          })
          .finally(() => $this.saving = false);
    },
    confirmDelete(cat) {
      let $this = this;
      this.$bvModal.msgBoxConfirm(this.msg('roomcategorydeleteconfirm'), {
        title: this.msg('Confirm deletion'),
        okVariant: 'danger',
        okTitle: this.msg('Yes, Delete'),
        cancelTitle: this.msg('cancel'),
        centered: true,
      }).then(confirmed => {
        if (confirmed !== true)
          return;
        $this.$store.dispatch('roomCategory/remove', cat.id).then(() => $this.refresh());
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.rs-card {
  padding: 0.5rem 0;
}

.rs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);

  &__hint {
    font-weight: 600;
    font-size: 1.05rem;
  }
}

.rs-empty {
  text-align: center;
  padding: 3rem 1rem;

  &__icon {
    color: #b9b9c3;
    margin-bottom: 0.75rem;
  }

  &__title {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__text {
    color: #6e6b7b;
    max-width: 420px;
    margin: 0 auto 1rem;
  }
}

.rs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  padding: 1.25rem;
}

.rs-cat {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(34, 41, 47, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  background: #fff;

  &:hover {
    box-shadow: 0 4px 16px rgba(34, 41, 47, 0.12);
    transform: translateY(-2px);
  }

  &__stripe {
    width: 6px;
    flex: 0 0 6px;
  }

  &__body {
    flex: 1;
    padding: 0.85rem 1rem;
    min-width: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  &__name {
    font-weight: 600;
    font-size: 1.05rem;
  }

  &__desc {
    color: #6e6b7b;
    font-size: 0.85rem;
    margin: 0.25rem 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.75rem;
    margin-top: 0.6rem;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.8rem;
    color: #5e5873;

    i {
      font-size: 0.75rem;
    }
  }

  &__actions {
    display: flex;
    align-items: flex-start;
    padding: 0.5rem 0.5rem 0 0;
  }
}

.rs-colorpick {
  height: 2.7rem;
  padding: 0.2rem 0.4rem;
}

.rs-modal-hint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #6e6b7b;
  font-size: 0.85rem;
}

.dark-layout {
  .rs-toolbar {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .rs-cat {
    background: #283046;
    border-color: rgba(255, 255, 255, 0.08);

    &__desc, &__chip {
      color: #b4b7bd;
    }
  }

  .rs-empty__text {
    color: #b4b7bd;
  }

  .rs-modal-hint {
    color: #b4b7bd;
  }
}
</style>
